import * as React from 'react';

import classnames from 'classnames';

import {MergeFieldsButton} from '../merge-fields/merge-fields-button';
import type {MergeField} from '../merge-fields/merge-fields-button';

import {CharacterLimit} from './character-limit';
import type {TextFieldProps} from './textfield';
import {TextField} from './textfield';

import styles from './textfield-with-character-limit.module.css';

type Props = TextFieldProps & {
    id?: string;
    maximumCharacters: number;
    minimumCharacters?: number;
    isDisabled?: boolean;
    mergeFieldOptions?: MergeField[];
    hasNoSideBorders?: boolean;
};

export function TextfieldWithCharacterLimit(props: Props) {
    const {
        mergeFieldOptions,
        maximumCharacters,
        minimumCharacters,
        isDisabled,
        onChange,
        value,
        hasNoSideBorders = true,
        ...textFieldProps
    } = props;

    const [isFocused, setIsFocused] = React.useState(false);

    const textFieldRef = React.useRef<HTMLInputElement | null | undefined>(null);

    const handleAddMergeField = (
        mergeFieldValue: string,
        mergeFieldFallbackText?: string | null
    ) => {
        if (onChange) {
            const mergeFieldToInsert = mergeFieldFallbackText
                ? `{${mergeFieldValue}|"${mergeFieldFallbackText}"}`
                : `{${mergeFieldValue}}`;

            if (textFieldRef && textFieldRef.current) {
                const textField = textFieldRef.current;

                const cursorPosition = textField.selectionStart;
                // @ts-expect-error
                textField.setRangeText(mergeFieldToInsert, cursorPosition, cursorPosition);
                onChange(textField.value);

                // @ts-expect-error
                const newCursorPosition = cursorPosition + mergeFieldToInsert.length;
                textField.setSelectionRange(newCursorPosition, newCursorPosition);
                textField.focus();

                return;
            }

            // Fallback for when we don't have a ref to the textfield
            onChange(`${value}${mergeFieldToInsert}`);
        }
    };

    const styleName = classnames({
        [styles['textfield-with-character-limit']]: !hasNoSideBorders,
        [styles['textfield-with-character-limit--no-side-borders']]: hasNoSideBorders,
    });

    return (
        <div className={styleName}>
            <TextField
                multiline={true}
                noBorder={true}
                // @ts-expect-error
                disabled={isDisabled}
                value={value}
                onChange={onChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                {...textFieldProps}
                textFieldRef={textFieldRef}
            />
            {/* If there are merge field options, always show footer (unless disabled) */}
            {(isFocused || mergeFieldOptions) && !isDisabled ? (
                <div className={styles['character-limit']}>
                    <CharacterLimit
                        maximumCharacters={maximumCharacters}
                        minimumCharacters={minimumCharacters}
                        length={value.length}
                    />
                    {mergeFieldOptions && (
                        <MergeFieldsButton
                            onAddMergeField={handleAddMergeField}
                            options={mergeFieldOptions}
                        />
                    )}
                </div>
            ) : (
                /* Prevents height jumping when showing character limit */
                <div
                    className={
                        isDisabled ? styles['character-limit--disabled'] : styles['character-limit']
                    }
                ></div>
            )}
        </div>
    );
}
